import { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import { DateTime } from 'luxon';
import { ColDef, CellValueChangedEvent, GridApi, GetRowIdParams } from 'ag-grid-community';
import { DataGrid } from '../../../../ui/data-grid';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useModels } from '../../../../../lib/supabase/models/supabaseModelService';
import {
  useFanGainEntriesForMonth,
  useUpsertFanGainEntry,
  useRealtimeFanGainManager,
  useFanGainAssignments,
  useFanGainUserOrder,
} from '../../../../../lib/supabase/fan-gain/supabaseFanGainService';
import { useUsers } from '../../../../../lib/supabase/users/supabaseUserService';
import { gridCssClasses } from './utils/fanGainGridConfig';
import './fan-gain-spreadsheet.css';

interface FanGainGGSpreadsheetProps {
  defaultMonth: number;
  defaultYear: number;
}

interface ModelUserInfo {
  user_id: string;
  user_name: string;
  order_index: number;
  assignment_index: number;
}

interface Model {
  id: string;
  name: string;
}

interface GridRowData {
  id: string;
  model: string;
  model_id?: string;
  is_user_header?: boolean;
  is_total_row?: boolean;
  row_type: 'total' | 'user_header' | 'model';
  [key: string]: string | number | boolean | undefined;
}


export function FanGainGGDataSpreadsheet({ defaultMonth, defaultYear }: FanGainGGSpreadsheetProps) {
  const { user } = useAuth();
  const gridRef = useRef<GridApi | null>(null);

  const month = useMemo(() => defaultMonth, [defaultMonth]);
  const year = useMemo(() => defaultYear, [defaultYear]);

  // Use refs to store current month/year values that callbacks can access
  const monthRef = useRef(month);
  const yearRef = useRef(year);
  const daysInMonthRef = useRef(0);

  // Track initial data load to avoid unnecessary transactions during setup
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Data
  const { data: allModels = [] } = useModels();
  const { data: users = [] } = useUsers();
  const { data: fanGainAssignments = [] } = useFanGainAssignments();
  const { data: userOrder = [] } = useFanGainUserOrder();

  // GG Swap entries (gg-swaps type)
  const { data: ggEntries = [] } = useFanGainEntriesForMonth(year, month, 'gg-swaps');

  const upsertEntryMutation = useUpsertFanGainEntry();

  // Real-time updates
  useRealtimeFanGainManager({
    enabled: true,
    ignoreuser_id: user?.id
  });

  // Inject CSS for grid styling
  useEffect(() => {
    const styleId = 'fan-gain-grid-styles';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = gridCssClasses;
      document.head.appendChild(styleElement);
    }

    return () => {
      // Cleanup on unmount
      const element = document.getElementById(styleId);
      if (element) {
        element.remove();
      }
    };
  }, []);

  // Days in current month
  const daysInMonth = useMemo(() => {
    const dt = DateTime.fromObject({ year, month });
    return dt.daysInMonth || 31;
  }, [year, month]);

  // Update refs when values change
  useEffect(() => {
    monthRef.current = month;
    yearRef.current = year;
    daysInMonthRef.current = daysInMonth;
  }, [month, year, daysInMonth]);

  // Create fast lookup maps
  const modelUserMap = useMemo(() => {
    const map = new Map<string, ModelUserInfo>();
    const userOrderMap = new Map(userOrder.map(uo => [uo.user_id, uo.order_index]));

    fanGainAssignments.forEach(assignment => {
      const user = users.find(u => u.id === assignment.user_id);
      const userName = user?.full_name || 'Unknown User';
      const orderIndex = userOrderMap.get(assignment.user_id) || 0;

      map.set(assignment.model_id, {
        user_id: assignment.user_id,
        user_name: userName,
        order_index: orderIndex,
        assignment_index: assignment.index
      });
    });

    return map;
  }, [fanGainAssignments, users, userOrder]);

  // Filter and sort models efficiently
  const filteredModels = useMemo((): Model[] => {
    return allModels
      .filter(model =>
        (model.account_type || 'Normal') === 'Normal' &&
        !model.paused &&
        modelUserMap.has(model.id)
      )
      .sort((a, b) => {
        const userA = modelUserMap.get(a.id);
        const userB = modelUserMap.get(b.id);

        if (!userA && !userB) return a.name.localeCompare(b.name);
        if (!userA) return 1;
        if (!userB) return -1;

        const orderDiff = userA.order_index - userB.order_index;
        if (orderDiff !== 0) return orderDiff;

        return userA.assignment_index - userB.assignment_index;
      })
      .map(model => ({ id: model.id, name: model.name }));
  }, [allModels, modelUserMap]);

  // Create flat entries index for O(1) lookups
  const entriesIndex = useMemo(() => {
    const index = new Map<string, number>();

    ggEntries.forEach(entry => {
      const date = DateTime.fromISO(entry.date);
      const day = date.day;
      const key = `${entry.model_id}-day${day}`;
      index.set(key, entry.value || 0);
    });

    return index;
  }, [ggEntries]);

  // Calculate day totals efficiently
  const dayTotals = useMemo(() => {
    const totals = new Map<number, number>();

    for (let day = 1; day <= daysInMonth; day++) {
      let dayTotal = 0;
      filteredModels.forEach(model => {
        const key = `${model.id}-day${day}`;
        dayTotal += entriesIndex.get(key) || 0;
      });
      totals.set(day, dayTotal);
    }

    return totals;
  }, [filteredModels, entriesIndex, daysInMonth]);

  // Generate optimized row data
  const initialRowData: GridRowData[] = useMemo(() => {
    if (!filteredModels.length) return [];

    const rows: GridRowData[] = [];

    // Add total row first
    const totalRow: GridRowData = {
      id: 'total-row',
      model: 'TOTAL',
      row_type: 'total',
      is_total_row: true
    };

    for (let day = 1; day <= daysInMonth; day++) {
      totalRow[`day${day}`] = dayTotals.get(day) || 0;
    }

    rows.push(totalRow);

    // Group models by user
    const groupedByUser = new Map<string, { userId: string; userName: string; models: Model[]; orderIndex: number }>();

    filteredModels.forEach(model => {
      const userInfo = modelUserMap.get(model.id);
      if (userInfo) {
        if (!groupedByUser.has(userInfo.user_id)) {
          groupedByUser.set(userInfo.user_id, {
            userId: userInfo.user_id,
            userName: userInfo.user_name,
            models: [],
            orderIndex: userInfo.order_index
          });
        }
        groupedByUser.get(userInfo.user_id)!.models.push(model);
      }
    });

    // Sort users and add their rows
    const sortedUsers = Array.from(groupedByUser.values()).sort((a, b) => a.orderIndex - b.orderIndex);

    sortedUsers.forEach(({ userId, userName, models }) => {
      // Add user header row
      const userHeaderRow: GridRowData = {
        id: `user-${userId}`,
        model: userName,
        row_type: 'user_header',
        is_user_header: true
      };

      for (let day = 1; day <= daysInMonth; day++) {
        userHeaderRow[`day${day}`] = '';
      }

      rows.push(userHeaderRow);

      // Add model rows for this user
      models.forEach(model => {
        const row: GridRowData = {
          id: model.id,
          model: model.name,
          model_id: model.id,
          row_type: 'model'
        };

        for (let day = 1; day <= daysInMonth; day++) {
          const key = `${model.id}-day${day}`;
          row[`day${day}`] = entriesIndex.get(key) || 0;
        }

        rows.push(row);
      });
    });

    return rows;
  }, [filteredModels, modelUserMap, entriesIndex, dayTotals, daysInMonth]);

  // Handle data updates via transactions instead of full row data refresh
  useEffect(() => {
    if (!gridRef.current || isInitialLoad || !filteredModels.length) return;

    const updatedRows: GridRowData[] = [];

    // Update total row
    const totalRow: GridRowData = {
      id: 'total-row',
      model: 'TOTAL',
      row_type: 'total',
      is_total_row: true
    };

    for (let day = 1; day <= daysInMonth; day++) {
      totalRow[`day${day}`] = dayTotals.get(day) || 0;
    }

    updatedRows.push(totalRow);

    // Update model rows
    filteredModels.forEach(model => {
      const row: GridRowData = {
        id: model.id,
        model: model.name,
        model_id: model.id,
        row_type: 'model'
      };

      for (let day = 1; day <= daysInMonth; day++) {
        const key = `${model.id}-day${day}`;
        row[`day${day}`] = entriesIndex.get(key) || 0;
      }

      updatedRows.push(row);
    });

    // Use transaction to update all rows efficiently
    gridRef.current.applyTransaction({
      update: updatedRows
    });

  }, [entriesIndex, dayTotals, daysInMonth, filteredModels, isInitialLoad]);

  // Mark initial load as complete after first render
  useEffect(() => {
    if (initialRowData.length > 0 && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [initialRowData.length, isInitialLoad]);

  // Handle cell value changes with direct updates
  const handleCellValueChanged = useCallback(async (event: CellValueChangedEvent) => {
    const { data, colDef, newValue, oldValue } = event;
    if (!user?.id) return;
    if (data?.row_type !== 'model') return;

    const field = colDef.field;
    if (!field || !field.startsWith('day')) return;

    const dayNumber = parseInt(field.replace('day', ''));
    // Use refs to get current values
    if (isNaN(dayNumber) || dayNumber < 1 || dayNumber > daysInMonthRef.current) return;

    const model_id = data.model_id as string;
    const value = parseFloat(newValue) || 0;
    const prev = parseFloat(oldValue) || 0;
    if (value === prev) return;

    try {
      // Use refs to get current month and year
      const date = DateTime.fromObject({ year: yearRef.current, month: monthRef.current, day: dayNumber }, { zone: 'UTC' });

      console.log(date);
      await upsertEntryMutation.mutateAsync({
        model_id,
        date,
        type: 'gg-swaps',
        value
      });
    } catch (e) {
      // Revert on failure
      event.node.setDataValue(field, oldValue);
    }
  }, [user?.id, upsertEntryMutation]);

  // Essential for efficient updates - AG Grid uses this to identify rows
  const getRowId = useCallback((params: GetRowIdParams) => {
    return params.data.id;
  }, []);

  // Column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        headerName: '',
        field: 'model',
        pinned: 'left',
        width: 150,
        resizable: false,
        sortable: false,
        filter: false,
        menuTabs: [],
        suppressMovable: true,
        editable: false,
        cellRenderer: (params: any) => {
          if (params.data?.is_total_row) {
            return '';
          }
          return params.value;
        },
        cellStyle: {
          textAlign: 'left',
          fontFamily: 'Arial, sans-serif',
          fontWeight: 700,
          fontSize: '15px',
          paddingLeft: 4,
          paddingRight: 4
        }
      }
    ];

    // Add day columns
    for (let day = 1; day <= daysInMonth; day++) {
      const field = `day${day}`;
      cols.push({
        headerName: String(day),
        field,
        width: 90,
        type: 'numericColumn',
        cellClass: 'text-center ag-cell-no-focus',
        headerClass: 'day-header',
        resizable: false,
        sortable: false,
        filter: false,
        menuTabs: [],
        suppressMovable: true,
        editable: (params: any) => params.data?.row_type === 'model',
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 0,
          precision: 0,
          step: 1
        },
        valueParser: (params) => {
          const value = params.newValue;
          if (value === '' || value == null) return 0;
          const parsed = Number(value);
          return isNaN(parsed) ? 0 : Math.max(0, Math.floor(parsed));
        },
        cellStyle: (params) => {
          const isZeroValue = params.value === 0 || params.value === '';

          const style: any = {
            textAlign: 'right',
            fontFamily: 'Arial, sans-serif',
            fontWeight: 700,
            fontSize: '15px',
            paddingRight: 4
          };

          // Let CSS classes handle total row and user header styling
          // Only add zero value styling for regular model rows
          if (!params.data?.is_total_row && !params.data?.is_user_header && isZeroValue) {
            style.color = '#9ca3af';
          }

          return style;
        },
        valueFormatter: (params) => {
          if (params.data?.is_user_header) {
            return '';
          }
          if (typeof params.value === 'number' && params.value > 0) {
            return params.value.toLocaleString();
          }
          return '';
        }
      });
    }

    // Add duplicate of the first column at the end
    cols.push({
      headerName: '',
      field: 'model',
      pinned: 'left',
      width: 150,
      resizable: false,
      sortable: false,
      filter: false,
      menuTabs: [],
      suppressMovable: true,
      editable: false,
      cellRenderer: (params: any) => {
        if (params.data?.is_total_row) {
          return 'TOTAL';
        }
        return params.value;
      },
      cellStyle: {
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif',
        fontWeight: 700,
        fontSize: '15px',
        paddingLeft: 4,
        paddingRight: 4
      }
    });

    return cols;
  }, [daysInMonth]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col">
        <DataGrid
          columnDefs={columnDefs}
          rowData={initialRowData}
          height={'calc(100vh - 180px)'}
          className="min-h-[400px]"
          defaultColDef={{
            resizable: false,
            sortable: false,
            filter: false,
            menuTabs: [],
            suppressMovable: true,
            suppressHeaderFilterButton: true,
            suppressHeaderMenuButton: true,
            cellClass: 'ag-cell-no-focus'
          }}
          gridOptions={{
            // Core performance settings
            animateRows: false,
            suppressColumnVirtualisation: false,
            suppressRowVirtualisation: false,
            rowBuffer: 10,

            // Change detection optimization
            getRowId: getRowId,

            // Rendering optimizations
            suppressCellFocus: false,
            ensureDomOrder: true,
            suppressScrollOnNewData: true,
            suppressLoadingOverlay: true,
            suppressNoRowsOverlay: true,

            // Update optimizations
            asyncTransactionWaitMillis: 50,
            suppressAggFuncInHeader: true,

            // Memory optimization
            maxConcurrentDatasourceRequests: 2,
            cacheBlockSize: 100,
            maxBlocksInCache: 10,
            purgeClosedRowNodes: true,

            // Viewport optimization
            viewportRowModelPageSize: 100,
            viewportRowModelBufferSize: 20,

            // Cell selection and editing
            cellSelection: true,
            suppressCopyRowsToClipboard: false,
            suppressCopySingleCellRanges: false,

            // Event handlers
            onCellValueChanged: handleCellValueChanged,

            // Row styling
            rowClassRules: {
              'fan-gain-totals-row': (params) => params.data?.is_total_row,
              'fan-gain-user-header': (params) => params.data?.is_user_header,
              'fan-gain-model-row': (params) => params.data?.row_type === 'model'
            },

            // Grid layout
            domLayout: 'normal',
            headerHeight: 42,
            rowHeight: 25,

            onGridReady: (params) => {
              gridRef.current = params.api;

              // Performance optimization: enable virtualization
              if (params.api.setGridOption) {
                try {
                  params.api.setGridOption('suppressCellFocus', false);
                } catch (e) {
                  console.log('Grid option not supported:', e);
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
}

export default FanGainGGDataSpreadsheet;